import React, { useMemo } from "react";
import {
  HiOutlineBan,
  HiOutlineClock,
  HiOutlineExclamation,
  HiOutlineOfficeBuilding,
  HiOutlineShieldExclamation,
} from "react-icons/hi";
import { classNames } from "Utils/utils";
import { formatMetricValue } from "pages/PlatformAdmin/Dashboard/platformDashboard.utils";
import { filtersMatchTenantWidget } from "./tenantsList.utils";

const WIDGETS = [
  {
    id: "all",
    label: "All",
    statKey: "total_tenants",
    icon: HiOutlineOfficeBuilding,
  },
  {
    id: "active",
    label: "Active",
    statKey: "active_tenants",
    icon: HiOutlineOfficeBuilding,
  },
  {
    id: "suspended",
    label: "Suspended",
    statKey: "suspended",
    icon: HiOutlineBan,
  },
  {
    id: "trial",
    label: "On trial",
    statKey: "on_trial",
    icon: HiOutlineClock,
  },
  {
    id: "readOnly",
    label: "Read-only",
    statKey: "read_only",
    icon: HiOutlineShieldExclamation,
  },
];

const SummaryChip = ({
  label,
  value,
  icon: Icon,
  isActive,
  variant = "default",
  onClick,
  hint = null,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={isActive}
    className={classNames(
      "inline-flex min-w-0 flex-1 items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
      variant === "attention"
        ? isActive
          ? "border-amber-400 bg-amber-50"
          : "border-amber-200 bg-amber-50/40 hover:border-amber-300"
        : isActive
          ? "border-indigo-400 bg-indigo-50"
          : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
    )}
  >
    <Icon
      className={classNames(
        "h-4 w-4 shrink-0",
        variant === "attention" ? "text-amber-600" : "text-indigo-600"
      )}
      aria-hidden="true"
    />
    <span className="min-w-0 flex-1">
      <span className="block truncate text-xs font-medium text-gray-500">{label}</span>
      <span className="flex items-baseline gap-1.5">
        <span className="text-lg font-semibold leading-tight text-gray-900">
          {value}
        </span>
        {hint ? (
          <span className="truncate text-[10px] font-medium text-amber-700">{hint}</span>
        ) : null}
      </span>
    </span>
  </button>
);

const SummarySkeleton = () => (
  <div className="mb-3 flex animate-pulse gap-2 overflow-x-auto pb-1">
    {Array.from({ length: 6 }).map((_, index) => (
      <div
        key={index}
        className="h-[52px] min-w-[7rem] flex-1 rounded-lg border border-gray-200 bg-gray-100"
      />
    ))}
  </div>
);

const TenantsListSummary = ({
  stats = null,
  attentionCount = 0,
  loading = false,
  filters,
  onWidgetSelect,
  onAttentionSelect,
}) => {
  const activeWidget = useMemo(() => {
    const presetMatch = WIDGETS.find((widget) =>
      filtersMatchTenantWidget(filters, widget.id)
    );

    return presetMatch?.id || null;
  }, [filters]);

  if (loading) {
    return <SummarySkeleton />;
  }

  return (
    <div className="mb-3 flex flex-wrap gap-2">
      {WIDGETS.map((widget) => (
        <SummaryChip
          key={widget.id}
          label={widget.label}
          value={formatMetricValue(stats?.[widget.statKey])}
          icon={widget.icon}
          isActive={activeWidget === widget.id}
          onClick={() => onWidgetSelect(widget.id)}
        />
      ))}

      {attentionCount > 0 ? (
        <SummaryChip
          label="Attention"
          value={formatMetricValue(attentionCount)}
          icon={HiOutlineExclamation}
          hint="Trials & billing"
          variant="attention"
          isActive={false}
          onClick={onAttentionSelect}
        />
      ) : null}
    </div>
  );
};

export default React.memo(TenantsListSummary);
